import type { TrustStack as TrustStackData } from '../types';

type Variant = 'full' | 'compact' | 'inline';

type Tone = 'good' | 'warn' | 'bad' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  bad: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
};

function toneFor(label: string): Tone {
  if (['High', 'Verified', 'Current'].includes(label)) return 'good';
  if (['Medium', 'Representative', 'Partial', 'Aging'].includes(label)) return 'warn';
  // Inactive is the WORST freshness state (>90 days), so it belongs with bad.
  if (['Low', 'Limited', 'Stale', 'Inactive'].includes(label)) return 'bad';
  return 'neutral';
}

function Chip({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${TONE_CLASS[toneFor(value)]}`}
      title={tooltip}
    >
      <span className="opacity-60">{label}:</span>
      <span>{value}</span>
    </span>
  );
}

function formatDataPeriod(dp: TrustStackData['data_period']): string {
  if (!dp) return 'Awaiting first scoring cycle';
  const fmt = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
    });
  return `${fmt(dp.start)} — ${fmt(dp.end)}`;
}

function formatLastUpdated(iso: string | null): string {
  if (!iso) return 'No scoring yet';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function confidenceTooltip(ts: TrustStackData): string {
  const b = ts.confidence.breakdown;
  return (
    `Composite of:\n` +
    `• DAR volume: ${b.dar_volume.upload_count} uploads (last 30d) → ${b.dar_volume.weighted.toFixed(1)}/35\n` +
    `• Submission consistency: ${b.submission_consistency.submitted_days}/${b.submission_consistency.expected_days} expected days → ${b.submission_consistency.weighted.toFixed(1)}/35\n` +
    `• Freshness: ${b.freshness.days_since_last_upload ?? '—'} days since last upload → ${b.freshness.weighted.toFixed(1)}/20\n` +
    `• Site Context: ${b.site_context_completeness.completed_count}/${b.site_context_completeness.active_site_count} cards completed → ${b.site_context_completeness.weighted.toFixed(1)}/10`
  );
}

export default function TrustStack({
  data, variant = 'compact', className = '',
}: { data: TrustStackData | null | undefined; variant?: Variant; className?: string }) {
  if (!data) return null;

  if (variant === 'inline') {
    return (
      <span className={`text-xs text-slate-500 ${className}`}>
        Confidence: <span className="font-medium text-slate-700">{data.confidence.label}</span>
        {' · '}
        Coverage: <span className="font-medium text-slate-700">{data.coverage.label}</span>
        {' · '}
        Freshness: <span className="font-medium text-slate-700">{data.freshness.label}</span>
      </span>
    );
  }

  const chips = (
    <div className="flex flex-wrap gap-2">
      <Chip
        label="Confidence"
        value={data.confidence.label}
        tooltip={confidenceTooltip(data)}
      />
      <Chip
        label="Coverage"
        value={data.coverage.label}
        tooltip={`${data.coverage.sites_with_recent_dar}/${data.coverage.total_active_sites} sites had a DAR in the last 30 days.`}
      />
      <Chip
        label="Freshness"
        value={data.freshness.label}
        tooltip={
          data.freshness.days_since === null
            ? 'No DARs uploaded yet.'
            : `Last DAR was ${data.freshness.days_since} day${data.freshness.days_since === 1 ? '' : 's'} ago.`
        }
      />
      <Chip
        label="Model"
        value={data.model_version}
        tooltip="Methodology version; bumped when the scoring formula changes."
      />
    </div>
  );

  if (variant === 'compact') {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        {chips}
        <div className="text-xs text-slate-400">
          Last updated {formatLastUpdated(data.last_updated)} · Data {formatDataPeriod(data.data_period)}
        </div>
      </div>
    );
  }

  // full
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${className}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Trust labels
      </div>
      <div className="mt-3">{chips}</div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-slate-400">Last Updated</div>
          <div className="font-medium text-slate-700">{formatLastUpdated(data.last_updated)}</div>
        </div>
        <div>
          <div className="text-slate-400">Data Period</div>
          <div className="font-medium text-slate-700">{formatDataPeriod(data.data_period)}</div>
        </div>
      </div>
    </div>
  );
}
